import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, CheckCircle2, ShieldCheck, Truck, Plus as PlusIcon, Search } from "lucide-react";
import { DateInputIT } from "@/components/DateInputIT";
import { fmtIT } from "@/lib/dateIT";

const euro = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const CONTI = [{ v: "INTESA_SEPARATO", l: "Conto Separato — Intesa" }, { v: "INTESA_GESTIONE", l: "Conto di Gestione — Intesa" }];

/* ============================ BONIFICI ============================ */
export function BonificiTab() {
  const [rows, setRows] = useState([]);
  const [conto, setConto] = useState("all");
  const [soloVerif, setSoloVerif] = useState(false);
  const [dlg, setDlg] = useState(null);
  const load = () => {
    const params = {};
    if (conto !== "all") params.conto = conto;
    if (soloVerif) params.da_verificare = "true";
    api.get("/contabilita/bonifici", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [conto, soloVerif]);
  const empty = { conto: "INTESA_SEPARATO", tipo: "entrata", data: new Date().toISOString().slice(0, 10), importo: 0, descrizione: "", riferimento_polizza: "", beneficiario_ordinante: "", da_verificare: true, note: "" };
  const save = async () => {
    try {
      if (dlg.id) await api.put(`/contabilita/bonifici/${dlg.id}`, dlg);
      else await api.post("/contabilita/bonifici", dlg);
      toast.success("Bonifico salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const verifica = async (r) => { await api.post(`/contabilita/bonifici/${r.id}/verifica`); toast.success("Verificato"); load(); };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/contabilita/bonifici/${r.id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={conto} onValueChange={setConto}><SelectTrigger className="w-56" data-testid="bon-filter-conto"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i conti</SelectItem>{CONTI.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
          <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={soloVerif} onCheckedChange={setSoloVerif} data-testid="bon-filter-verif" /> Solo da verificare</label>
        </div>
        <Button data-testid="bon-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuovo bonifico</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Conto</TableHead><TableHead>Tipo</TableHead><TableHead>Importo</TableHead><TableHead>Descrizione</TableHead><TableHead>Polizza</TableHead><TableHead>Screenshot</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`bon-row-${r.id}`} className={r.da_verificare ? "bg-orange-50/40" : ""}>
                <TableCell>{fmtIT(r.data)}</TableCell>
                <TableCell className="text-xs">{CONTI.find((c) => c.v === r.conto)?.l || r.conto}</TableCell>
                <TableCell><span className={r.tipo === "entrata" ? "text-green-600 text-xs" : "text-red-600 text-xs"}>{r.tipo}</span></TableCell>
                <TableCell>{euro(r.importo)}</TableCell>
                <TableCell className="max-w-[180px] truncate">{r.descrizione || "—"}</TableCell>
                <TableCell>{r.riferimento_polizza || "—"}</TableCell>
                <TableCell className="w-44"><FileUpload modulo="bonifico" refId={r.id} compact testid={`bon-file-${r.id}`} accept=".png,.jpg,.jpeg,.pdf" /></TableCell>
                <TableCell>{r.da_verificare ? <span className="text-orange-600 text-xs">Da verificare</span> : <span className="text-green-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Verificato</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {r.da_verificare && <button onClick={() => verifica(r)} className="text-emerald-600 hover:text-emerald-700 mr-3" data-testid={`bon-verifica-${r.id}`} title="Segna verificato"><CheckCircle2 className="h-4 w-4" /></button>}
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Nessun bonifico registrato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="bon-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuovo"} bonifico</DialogTitle><DialogDescription>Bonifico sui conti Banca Intesa (Separato/Gestione).</DialogDescription></DialogHeader>
          {dlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Conto</Label><Select value={dlg.conto} onValueChange={(v) => setDlg({ ...dlg, conto: v })}><SelectTrigger data-testid="bon-conto"><SelectValue /></SelectTrigger><SelectContent>{CONTI.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Tipo</Label><Select value={dlg.tipo} onValueChange={(v) => setDlg({ ...dlg, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="entrata">Entrata</SelectItem><SelectItem value="uscita">Uscita</SelectItem></SelectContent></Select></div>
              <div><Label>Data</Label><DateInputIT value={dlg.data} onChange={(v) => setDlg({ ...dlg, data: v })} /></div>
              <div><Label>Importo (€)</Label><Input type="number" value={dlg.importo} onChange={(e) => setDlg({ ...dlg, importo: e.target.value })} data-testid="bon-importo" /></div>
              <div className="col-span-2"><Label>Descrizione</Label><Input value={dlg.descrizione} onChange={(e) => setDlg({ ...dlg, descrizione: e.target.value })} /></div>
              <div><Label>Rif. polizza</Label><Input value={dlg.riferimento_polizza} onChange={(e) => setDlg({ ...dlg, riferimento_polizza: e.target.value })} /></div>
              <div><Label>Beneficiario/Ordinante</Label><Input value={dlg.beneficiario_ordinante} onChange={(e) => setDlg({ ...dlg, beneficiario_ordinante: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-2"><Checkbox checked={dlg.da_verificare} onCheckedChange={(v) => setDlg({ ...dlg, da_verificare: !!v })} /><Label>Da verificare (giorno successivo)</Label></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="bon-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ ESTRATTI CONTO ============================ */
export function EstrattiTab() {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const load = (pg = 1, append = false) => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    p.set("page", pg);
    p.set("page_size", 50);
    return api.get(`/contabilita/estratti?${p}`).then((r) => {
      setRows((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      setHasMore(r.data.has_more); setTotal(r.data.total); setPage(r.data.page);
    }).catch(() => setRows([]));
  };
  useEffect(() => { load(1, false); }, []); // eslint-disable-line
  const qFirst = useRef(true);
  useEffect(() => {
    if (qFirst.current) { qFirst.current = false; return; }
    const t = setTimeout(() => load(1, false), 400);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line
  const empty = { conto: "INTESA_GESTIONE", data: new Date().toISOString().slice(0, 10), tipo: "giornaliero", inviato_a_mariangela: false, note: "" };
  const save = async () => {
    try {
      if (dlg.id) await api.put(`/contabilita/estratti/${dlg.id}`, dlg);
      else await api.post("/contabilita/estratti", dlg);
      toast.success("Estratto salvato"); setDlg(null); load(1, false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/contabilita/estratti/${r.id}`); load(1, false); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3">
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per data o nota…" className="pl-9 bg-white" data-testid="est-search" />
        </div>
        <Button data-testid="est-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuovo estratto</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Conto</TableHead><TableHead>Tipo</TableHead><TableHead>PDF</TableHead><TableHead>Inviato</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`est-row-${r.id}`}>
                <TableCell>{fmtIT(r.data)}</TableCell>
                <TableCell className="text-xs">{CONTI.find((c) => c.v === r.conto)?.l || r.conto}</TableCell>
                <TableCell className="capitalize">{r.tipo}</TableCell>
                <TableCell className="w-44"><FileUpload modulo="estratto_conto" refId={r.id} compact testid={`est-file-${r.id}`} accept=".pdf" /></TableCell>
                <TableCell>{r.tipo === "trimestrale" ? (r.inviato_a_mariangela ? <span className="text-green-600 text-xs">Inviato</span> : <span className="text-orange-600 text-xs">Da inviare</span>) : <span className="text-slate-400 text-xs">—</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessun estratto registrato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-slate-400" data-testid="est-count">{rows.length} di {total} estratti</p>
        {hasMore && <Button variant="outline" size="sm" onClick={() => load(page + 1, true)} data-testid="est-load-more">Carica altri</Button>}
      </div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="est-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuovo"} estratto conto</DialogTitle><DialogDescription>Estratto conto giornaliero o trimestrale.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Conto</Label><Select value={dlg.conto} onValueChange={(v) => setDlg({ ...dlg, conto: v })}><SelectTrigger data-testid="est-conto"><SelectValue /></SelectTrigger><SelectContent>{CONTI.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Tipo</Label><Select value={dlg.tipo} onValueChange={(v) => setDlg({ ...dlg, tipo: v })}><SelectTrigger data-testid="est-tipo"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="giornaliero">Giornaliero</SelectItem><SelectItem value="trimestrale">Trimestrale</SelectItem></SelectContent></Select></div>
              <div><Label>Data</Label><DateInputIT value={dlg.data} onChange={(v) => setDlg({ ...dlg, data: v })} /></div>
              {dlg.tipo === "trimestrale" && <div className="flex items-end gap-2 pb-2"><Checkbox checked={dlg.inviato_a_mariangela} onCheckedChange={(v) => setDlg({ ...dlg, inviato_a_mariangela: !!v })} /><Label>Inviato</Label></div>}
              <div className="col-span-2"><Label>Note</Label><Textarea value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="est-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ SUBAGENZIA ============================ */
export function SubagenziaTab() {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/contabilita/subagenzia").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const empty = { settimana: "", righe: [], stato: "aperto", data_consegna: "", note: "" };
  const addRiga = () => setDlg({ ...dlg, righe: [...dlg.righe, { cliente: "", polizza: "", compagnia: "", importo: 0 }] });
  const setRiga = (i, k, v) => { const rg = [...dlg.righe]; rg[i] = { ...rg[i], [k]: v }; setDlg({ ...dlg, righe: rg }); };
  const delRiga = (i) => setDlg({ ...dlg, righe: dlg.righe.filter((_, j) => j !== i) });
  const totale = dlg ? dlg.righe.reduce((a, r) => a + (Number(r.importo) || 0), 0) : 0;
  const save = async () => {
    if (!dlg.settimana?.trim()) return toast.error("Indica la settimana");
    try {
      const payload = { ...dlg, righe: dlg.righe.map((r) => ({ ...r, importo: Number(r.importo) || 0 })) };
      if (dlg.id) await api.put(`/contabilita/subagenzia/${dlg.id}`, payload);
      else await api.post("/contabilita/subagenzia", payload);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/contabilita/subagenzia/${r.id}`); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="sub-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuovo modulo settimanale</Button></div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Settimana</TableHead><TableHead>Incassi</TableHead><TableHead>Totale</TableHead><TableHead>Stato</TableHead><TableHead>Consegna</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`sub-row-${r.id}`}>
                <TableCell className="font-medium">{r.settimana}</TableCell>
                <TableCell>{(r.righe || []).length}</TableCell>
                <TableCell>{euro(r.totale)}</TableCell>
                <TableCell>{r.stato === "consegnato" ? <span className="text-green-600 text-xs">Consegnato</span> : <span className="text-orange-600 text-xs">Aperto</span>}</TableCell>
                <TableCell>{fmtIT(r.data_consegna)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessun modulo. Ricorda: consegna il giovedì mattina ad Antonio.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="sub-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuovo"} modulo incassi Subagenzia</DialogTitle><DialogDescription>Incassi settimanali da consegnare il giovedì ad Antonio.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Settimana</Label><Input value={dlg.settimana} onChange={(e) => setDlg({ ...dlg, settimana: e.target.value })} placeholder="es. 2026-W28" data-testid="sub-settimana" /></div>
                <div><Label>Stato</Label><Select value={dlg.stato} onValueChange={(v) => setDlg({ ...dlg, stato: v })}><SelectTrigger data-testid="sub-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="aperto">Aperto</SelectItem><SelectItem value="consegnato">Consegnato</SelectItem></SelectContent></Select></div>
                <div><Label>Data consegna</Label><DateInputIT value={dlg.data_consegna} onChange={(v) => setDlg({ ...dlg, data_consegna: v })} /></div>
              </div>
              <div className="flex items-center justify-between"><Label>Incassi</Label><Button size="sm" variant="outline" onClick={addRiga} data-testid="sub-add-riga"><PlusIcon className="h-3.5 w-3.5 mr-1" />Riga</Button></div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dlg.righe.map((rg, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4" placeholder="Cliente" value={rg.cliente} onChange={(e) => setRiga(i, "cliente", e.target.value)} />
                    <Input className="col-span-3" placeholder="Polizza" value={rg.polizza} onChange={(e) => setRiga(i, "polizza", e.target.value)} />
                    <Input className="col-span-2" placeholder="Compagnia" value={rg.compagnia} onChange={(e) => setRiga(i, "compagnia", e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="€" value={rg.importo} onChange={(e) => setRiga(i, "importo", e.target.value)} />
                    <button className="col-span-1 text-slate-400 hover:text-red-500" onClick={() => delRiga(i)}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {dlg.righe.length === 0 && <div className="text-xs text-slate-400">Nessun incasso. Aggiungi una riga.</div>}
              </div>
              <div className="text-right text-sm font-medium">Totale: {euro(totale)}</div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="sub-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ QUIETANZAMENTO ============================ */
export function QuietanzamentoTab({ compagnie = [] }) {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/contabilita/quietanzamento").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const empty = { compagnia: "", tipo: "decade", periodo: "", importo: 0, scadenza: "", stato: "da_gestire", responsabile: "Mariella", note: "" };
  const save = async () => {
    if (!dlg.compagnia?.trim()) return toast.error("Indica la compagnia");
    try {
      if (dlg.id) await api.put(`/contabilita/quietanzamento/${dlg.id}`, dlg);
      else await api.post("/contabilita/quietanzamento", dlg);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/contabilita/quietanzamento/${r.id}`); load(); };
  const STATO = { da_gestire: "Da gestire", inviato: "Inviato", chiuso: "Chiuso" };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="qui-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuova quietanza</Button></div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Compagnia</TableHead><TableHead>Tipo</TableHead><TableHead>Periodo</TableHead><TableHead>Importo</TableHead><TableHead>Scadenza</TableHead><TableHead>Responsabile</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`qui-row-${r.id}`}>
                <TableCell className="font-medium">{r.compagnia}</TableCell>
                <TableCell className="capitalize">{r.tipo}</TableCell>
                <TableCell>{r.periodo || "—"}</TableCell>
                <TableCell>{euro(r.importo)}</TableCell>
                <TableCell>{fmtIT(r.scadenza)}</TableCell>
                <TableCell>{r.responsabile || "—"}</TableCell>
                <TableCell><span className={r.stato === "chiuso" ? "text-green-600 text-xs" : r.stato === "inviato" ? "text-blue-600 text-xs" : "text-orange-600 text-xs"}>{STATO[r.stato]}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Nessuna quietanza/decade registrata.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="qui-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuova"} quietanza / decade</DialogTitle><DialogDescription>Decadi, quindicine e quietanzamento per compagnia.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Compagnia</Label>
                {compagnie.length > 0 ? (
                  <Select value={dlg.compagnia} onValueChange={(v) => setDlg({ ...dlg, compagnia: v })}><SelectTrigger data-testid="qui-compagnia"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{compagnie.map((c) => <SelectItem key={c.compagnia_id || c.nome} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent></Select>
                ) : <Input value={dlg.compagnia} onChange={(e) => setDlg({ ...dlg, compagnia: e.target.value })} data-testid="qui-compagnia" />}
              </div>
              <div><Label>Tipo</Label><Select value={dlg.tipo} onValueChange={(v) => setDlg({ ...dlg, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="decade">Decade</SelectItem><SelectItem value="quindicina">Quindicina</SelectItem><SelectItem value="quietanzamento">Quietanzamento</SelectItem></SelectContent></Select></div>
              <div><Label>Periodo</Label><Input value={dlg.periodo} onChange={(e) => setDlg({ ...dlg, periodo: e.target.value })} placeholder="1a decade luglio" /></div>
              <div><Label>Importo (€)</Label><Input type="number" value={dlg.importo} onChange={(e) => setDlg({ ...dlg, importo: e.target.value })} data-testid="qui-importo" /></div>
              <div><Label>Scadenza</Label><DateInputIT value={dlg.scadenza} onChange={(v) => setDlg({ ...dlg, scadenza: v })} /></div>
              <div><Label>Responsabile</Label><Input value={dlg.responsabile} onChange={(e) => setDlg({ ...dlg, responsabile: e.target.value })} /></div>
              <div><Label>Stato</Label><Select value={dlg.stato} onValueChange={(v) => setDlg({ ...dlg, stato: v })}><SelectTrigger data-testid="qui-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="da_gestire">Da gestire</SelectItem><SelectItem value="inviato">Inviato</SelectItem><SelectItem value="chiuso">Chiuso</SelectItem></SelectContent></Select></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="qui-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ ASSIMOCO ============================ */
export function AssimocoTab() {
  const [resi, setResi] = useState([]);
  const [invii, setInvii] = useState([]);
  const [resoDlg, setResoDlg] = useState(null);
  const [invioDlg, setInvioDlg] = useState(null);
  const loadResi = () => api.get("/contabilita/assimoco/resi").then((r) => setResi(r.data)).catch(() => setResi([]));
  const loadInvii = () => api.get("/contabilita/assimoco/invii").then((r) => setInvii(r.data)).catch(() => setInvii([]));
  useEffect(() => { loadResi(); loadInvii(); }, []);

  // Resi
  const emptyReso = { polizza: "", cliente: "", importo: 0, data_reso: new Date().toISOString().slice(0, 10), motivo: "", stato: "in_gestione", note: "" };
  const saveReso = async () => {
    if (!resoDlg.polizza?.trim()) return toast.error("Indica la polizza");
    try {
      if (resoDlg.id) await api.put(`/contabilita/assimoco/resi/${resoDlg.id}`, resoDlg);
      else await api.post("/contabilita/assimoco/resi", resoDlg);
      toast.success("Salvato"); setResoDlg(null); loadResi();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delReso = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/contabilita/assimoco/resi/${r.id}`); loadResi(); };

  // Invii
  const emptyInvio = { data: new Date().toISOString().slice(0, 10), corriere: "", righe: [], stato: "in_preparazione", note: "" };
  const addRigaP = () => setInvioDlg({ ...invioDlg, righe: [...invioDlg.righe, { polizza: "", cliente: "", ramo: "RCA", otp_firma: false, categoria: "cartacea", incluso: true }] });
  const setRigaP = (i, k, v) => { const rg = [...invioDlg.righe]; rg[i] = { ...rg[i], [k]: v }; setInvioDlg({ ...invioDlg, righe: rg }); };
  const delRigaP = (i) => setInvioDlg({ ...invioDlg, righe: invioDlg.righe.filter((_, j) => j !== i) });
  const saveInvio = async () => {
    try {
      if (invioDlg.id) await api.put(`/contabilita/assimoco/invii/${invioDlg.id}`, invioDlg);
      else await api.post("/contabilita/assimoco/invii", invioDlg);
      toast.success("Salvato"); setInvioDlg(null); loadInvii();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delInvio = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/contabilita/assimoco/invii/${r.id}`); loadInvii(); };

  return (
    <div className="space-y-10">
      {/* TITOLI RESI */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg text-slate-800 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-600" />Titoli resi Assimoco</h3>
          <Button size="sm" data-testid="reso-add" onClick={() => setResoDlg({ ...emptyReso })}><Plus className="h-4 w-4 mr-1.5" />Nuovo reso</Button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Polizza</TableHead><TableHead>Cliente</TableHead><TableHead>Importo</TableHead><TableHead>Data reso</TableHead><TableHead>Motivo</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {resi.map((r) => (
                <TableRow key={r.id} data-testid={`reso-row-${r.id}`}>
                  <TableCell className="font-medium">{r.polizza}</TableCell>
                  <TableCell>{r.cliente || "—"}</TableCell>
                  <TableCell>{euro(r.importo)}</TableCell>
                  <TableCell>{fmtIT(r.data_reso)}</TableCell>
                  <TableCell className="max-w-[160px] truncate">{r.motivo || "—"}</TableCell>
                  <TableCell><span className={r.stato === "chiuso" ? "text-green-600 text-xs" : "text-orange-600 text-xs"}>{r.stato === "chiuso" ? "Chiuso" : "In gestione"}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => setResoDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delReso(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
              {resi.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nessun titolo reso.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* INVIO IN DIREZIONE */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg text-slate-800 flex items-center gap-2"><Truck className="h-5 w-5 text-indigo-600" />Invio polizze in Direzione</h3>
          <Button size="sm" data-testid="invio-add" onClick={() => setInvioDlg({ ...emptyInvio })}><Plus className="h-4 w-4 mr-1.5" />Nuovo invio</Button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Corriere</TableHead><TableHead>Polizze</TableHead><TableHead>Mancate OTP</TableHead><TableHead>Cartacee</TableHead><TableHead>Originali</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {invii.map((r) => (
                <TableRow key={r.id} data-testid={`invio-row-${r.id}`}>
                  <TableCell>{fmtIT(r.data)}</TableCell>
                  <TableCell>{r.corriere || "—"}</TableCell>
                  <TableCell>{r.conteggi?.da_inviare ?? 0}</TableCell>
                  <TableCell>{r.conteggi?.mancate_otp > 0 ? <span className="text-red-600 font-medium">{r.conteggi.mancate_otp}</span> : 0}</TableCell>
                  <TableCell>{r.conteggi?.cartacee ?? 0}</TableCell>
                  <TableCell>{r.conteggi?.originali ?? 0}</TableCell>
                  <TableCell><span className={r.stato === "inviato" ? "text-green-600 text-xs" : "text-orange-600 text-xs"}>{r.stato === "inviato" ? "Inviato" : "In preparazione"}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => setInvioDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delInvio(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
              {invii.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Nessun invio. Include RCA e Rami Elementari.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Dialog Reso */}
      <Dialog open={!!resoDlg} onOpenChange={(o) => !o && setResoDlg(null)}>
        <DialogContent data-testid="reso-dialog">
          <DialogHeader><DialogTitle>{resoDlg?.id ? "Modifica" : "Nuovo"} titolo reso</DialogTitle><DialogDescription>Titoli resi Assimoco.</DialogDescription></DialogHeader>
          {resoDlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Polizza</Label><Input value={resoDlg.polizza} onChange={(e) => setResoDlg({ ...resoDlg, polizza: e.target.value })} data-testid="reso-polizza" /></div>
              <div><Label>Cliente</Label><Input value={resoDlg.cliente} onChange={(e) => setResoDlg({ ...resoDlg, cliente: e.target.value })} /></div>
              <div><Label>Importo (€)</Label><Input type="number" value={resoDlg.importo} onChange={(e) => setResoDlg({ ...resoDlg, importo: e.target.value })} /></div>
              <div><Label>Data reso</Label><DateInputIT value={resoDlg.data_reso} onChange={(v) => setResoDlg({ ...resoDlg, data_reso: v })} /></div>
              <div className="col-span-2"><Label>Motivo</Label><Input value={resoDlg.motivo} onChange={(e) => setResoDlg({ ...resoDlg, motivo: e.target.value })} /></div>
              <div><Label>Stato</Label><Select value={resoDlg.stato} onValueChange={(v) => setResoDlg({ ...resoDlg, stato: v })}><SelectTrigger data-testid="reso-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="in_gestione">In gestione</SelectItem><SelectItem value="chiuso">Chiuso</SelectItem></SelectContent></Select></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setResoDlg(null)}>Annulla</Button><Button onClick={saveReso} data-testid="reso-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Invio */}
      <Dialog open={!!invioDlg} onOpenChange={(o) => !o && setInvioDlg(null)}>
        <DialogContent className="max-w-3xl" data-testid="invio-dialog">
          <DialogHeader><DialogTitle>{invioDlg?.id ? "Modifica" : "Nuovo"} invio in Direzione</DialogTitle><DialogDescription>Checklist polizze: firma OTP, cartacee e originali (RCA + Rami Elementari).</DialogDescription></DialogHeader>
          {invioDlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Data</Label><DateInputIT value={invioDlg.data} onChange={(v) => setInvioDlg({ ...invioDlg, data: v })} /></div>
                <div><Label>Corriere</Label><Input value={invioDlg.corriere} onChange={(e) => setInvioDlg({ ...invioDlg, corriere: e.target.value })} /></div>
                <div><Label>Stato</Label><Select value={invioDlg.stato} onValueChange={(v) => setInvioDlg({ ...invioDlg, stato: v })}><SelectTrigger data-testid="invio-stato"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="in_preparazione">In preparazione</SelectItem><SelectItem value="inviato">Inviato</SelectItem></SelectContent></Select></div>
              </div>
              <div className="flex items-center justify-between"><Label>Polizze (checklist OTP · categoria)</Label><Button size="sm" variant="outline" onClick={addRigaP} data-testid="invio-add-riga"><PlusIcon className="h-3.5 w-3.5 mr-1" />Polizza</Button></div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {invioDlg.righe.map((rg, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-3" placeholder="Polizza" value={rg.polizza} onChange={(e) => setRigaP(i, "polizza", e.target.value)} />
                    <Input className="col-span-3" placeholder="Cliente" value={rg.cliente} onChange={(e) => setRigaP(i, "cliente", e.target.value)} />
                    <Select value={rg.ramo} onValueChange={(v) => setRigaP(i, "ramo", v)}><SelectTrigger className="col-span-2 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="RCA">RCA</SelectItem><SelectItem value="Rami Elementari">Rami Elementari</SelectItem></SelectContent></Select>
                    <label className="col-span-1 flex items-center gap-1 text-xs" title="Firma OTP"><Checkbox checked={rg.otp_firma} onCheckedChange={(v) => setRigaP(i, "otp_firma", !!v)} /> OTP</label>
                    <Select value={rg.categoria} onValueChange={(v) => setRigaP(i, "categoria", v)}><SelectTrigger className="col-span-2 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cartacea">Cartacea</SelectItem><SelectItem value="originale">Originale</SelectItem></SelectContent></Select>
                    <button className="col-span-1 text-slate-400 hover:text-red-500" onClick={() => delRigaP(i)}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {invioDlg.righe.length === 0 && <div className="text-xs text-slate-400">Nessuna polizza. Aggiungi con "Polizza".</div>}
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
                Elenchi generati automaticamente: <b>Da inviare</b> {invioDlg.righe.filter((r) => r.incluso !== false).length} ·
                <b> Mancate OTP</b> {invioDlg.righe.filter((r) => r.incluso !== false && !r.otp_firma).length} ·
                <b> Cartacee</b> {invioDlg.righe.filter((r) => r.categoria === "cartacea").length} ·
                <b> Originali</b> {invioDlg.righe.filter((r) => r.categoria === "originale").length}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setInvioDlg(null)}>Annulla</Button><Button onClick={saveInvio} data-testid="invio-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
